// client/src/components/admin/project/CompletedProjectList.js
import React, { useState, useEffect, useCallback } from 'react';
import '../../../styles/admin/project/CompletedProject.css';
import ProjectDetailModal from '../../marketer/project/modals/ProjectDetailModal';
import ApplicationsViewModal from './ApplicationsViewModal';
import ResultStatusModal from './ResultStatusModal';
import CPSSettlementModal from './CPSSettlementModal';
import ProjectHistoryModal from './ProjectHistoryModal';

const API_URL = process.env.REACT_APP_API_URL;

export default function CompletedProjectList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // 모달 상태
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showApplicationsModal, setShowApplicationsModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showCPSModal, setShowCPSModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  // 검색 및 페이지네이션 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;

  // 프로젝트 선택 및 상세 정보 조회
  const handleSelectProject = useCallback(async (project) => {
    setSelectedProject(project);
    setLoadingDetail(true);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${project.id}/completion-detail`, {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        setSelectedProject({
          ...project,
          ...data.project
        });
      } else {
        console.error('상세 정보 조회 실패:', data.message);
      }
    } catch (error) {
      console.error('상세 정보 조회 오류:', error);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // 프로젝트 목록 조회
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: limit
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const res = await fetch(`${API_URL}/api/admin/projects/completed-list?${params}`, {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        setProjects(data.projects || []);
        setTotalCount(data.total || 0);
        setTotalPages(data.totalPages || 1);
        
        // 첫 번째 프로젝트 자동 선택 (상세 정보 포함)
        if (data.projects && data.projects.length > 0) {
          handleSelectProject(data.projects[0]);
        } else {
          setSelectedProject(null);
        }
      } else {
        alert('목록 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('완료된 프로젝트 목록 조회 오류:', error);
      alert('목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, handleSelectProject]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // 프로젝트 상세보기 모달
  const handleShowProjectDetail = () => {
    if (selectedProject?.fullProjectData) {
      setShowProjectModal(true);
    }
  };

  // 인플루언서 선정 계약 상세 모달
  const handleShowApplications = () => {
    if (selectedProject?.id) {
      setShowApplicationsModal(true);
    }
  };

  // 결과물 상태보기 모달
  const handleShowResultStatus = () => {
    if (selectedProject?.id) {
      setShowResultModal(true);
    }
  };

  // CPS 결산 상태보기 모달
  const handleShowCPSSettlement = () => {
    if (selectedProject?.id) {
      setShowCPSModal(true);
    }
  };

  // 검색 실행
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  // 검색 초기화
  const handleResetSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Enter 키로 검색
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 페이지 변경
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  // 날짜 시간 포맷팅
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  // 상태 한글 변환
  const getStatusText = (status) => {
    const statusMap = {
      draft: '작성중',
      open: '진행중',
      closed: '마감',
      canceled: '취소됨',
      archived: '보관됨',
      completed: '완료됨'
    };
    return statusMap[status] || status;
  };

  // 페이지 번호 생성 (현재 페이지 기준 앞뒤 2개씩)
  const getPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  return (
    <div className="completed-project-list">
      {/* 왼쪽: 프로젝트 목록 */}
      <div className="completed-project-list__sidebar">
        {/* 헤더 */}
        <div className="completed-project-list__sidebar-header">
          <h2 className="completed-project-list__sidebar-title">
            완료된 프로젝트 ({totalCount})
          </h2>
        </div>

        {/* 검색 */}
        <div className="completed-project-list__search">
          <div className="completed-project-list__search-controls">
            <input
              type="text"
              placeholder="프로젝트 번호 또는 광고주명 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
              className="completed-project-list__search-input"
            />
            <button
              onClick={handleSearch}
              className="completed-project-list__search-button"
            >
              검색
            </button>
          </div>
          {searchTerm && (
            <div className="completed-project-list__search-info">
              검색어: "{searchTerm}"
              <button
                onClick={handleResetSearch}
                className="completed-project-list__search-reset"
              >
                초기화
              </button>
            </div>
          )}
        </div>

        {/* 목록 */}
        <div className="completed-project-list__projects">
          {loading && (
            <div className="completed-project-list__loading">
              로딩 중...
            </div>
          )}

          {!loading && projects.length === 0 && (
            <div className="completed-project-list__empty">
              {searchTerm ? '검색 결과가 없습니다.' : '완료된 프로젝트가 없습니다.'}
            </div>
          )}

          {!loading && projects.map((project) => (
            <div
              key={project.id}
              onClick={() => handleSelectProject(project)}
              className={`completed-project-list__project-item ${
                selectedProject?.id === project.id ? 'completed-project-list__project-item--selected' : ''
              }`}
            >
              <div className="completed-project-list__project-number">
                {project.projectNumber}
              </div>
              <div className="completed-project-list__project-marketer">
                {project.marketerName}
              </div>
              <div className="completed-project-list__project-date">
                완료: {formatDate(project.completedAt)}
              </div>
            </div>
          ))}
        </div>

        {/* 페이지네이션 */}
        {!loading && totalPages > 1 && (
          <div className="completed-project-list__pagination">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="completed-project-list__pagination-btn"
            >
              처음
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="completed-project-list__pagination-btn"
            >
              이전
            </button>
            
            {getPageNumbers().map(pageNum => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`completed-project-list__pagination-btn ${
                  currentPage === pageNum ? 'completed-project-list__pagination-btn--active' : ''
                }`}
              >
                {pageNum}
              </button>
            ))}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="completed-project-list__pagination-btn"
            >
              다음
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="completed-project-list__pagination-btn"
            >
              마지막
            </button>
          </div>
        )}
      </div>

      {/* 오른쪽: 프로젝트 상세 */}
      <div className="completed-project-list__detail">
        {selectedProject ? (
          <>
            {/* 고정 헤더 및 버튼 영역 */}
            <div className="completed-project-list__detail-header">
              <h2 className="completed-project-list__detail-title">
                프로젝트 상세 정보
              </h2>

              {/* 버튼 영역 */}
              <div className="completed-project-list__actions">
              {/* 프로젝트 상세보기 버튼 */}
              <button
                onClick={handleShowProjectDetail}
                disabled={!selectedProject?.fullProjectData || loadingDetail}
                className={`completed-project-list__action-btn ${
                  selectedProject?.fullProjectData && !loadingDetail ? 'completed-project-list__action-btn--primary' : 'completed-project-list__action-btn--disabled'
                }`}
              >
                {loadingDetail ? (
                  <>⏳ 로딩 중...</>
                ) : (
                  <>📄 프로젝트 상세보기</>
                )}
              </button>

              {/* 인플루언서 선정 계약 상세 버튼 */}
              <button
                onClick={handleShowApplications}
                disabled={!selectedProject.id}
                className={`completed-project-list__action-btn ${
                  selectedProject.id ? 'completed-project-list__action-btn--success' : 'completed-project-list__action-btn--disabled'
                }`}
              >
                📊 인플루언서 선정 계약 상세
              </button>

              {/* 결과물 상태보기 버튼 */}
              <button
                onClick={handleShowResultStatus}
                disabled={!selectedProject.id}
                className={`completed-project-list__action-btn ${
                  selectedProject.id ? 'completed-project-list__action-btn--warning' : 'completed-project-list__action-btn--disabled'
                }`}
              >
                📋 결과물 상태보기
              </button>

              {/* CPS 결산 상태보기 버튼 */}
              <button
                onClick={handleShowCPSSettlement}
                disabled={!selectedProject.id}
                className={`completed-project-list__action-btn ${
                  selectedProject.id ? 'completed-project-list__action-btn--purple' : 'completed-project-list__action-btn--disabled'
                }`}
              >
                💰 CPS 결산 상태보기
              </button>

              {/* 이력 관리 버튼 */}
              <button
                onClick={() => setShowHistoryModal(true)}
                disabled={!selectedProject.id}
                className={`completed-project-list__action-btn ${
                  selectedProject.id ? 'completed-project-list__action-btn--indigo' : 'completed-project-list__action-btn--disabled'
                }`}
              >
                📋 이력 관리
              </button>
            </div>
            </div>

            {/* 스크롤 가능한 내용 영역 */}
            <div className="completed-project-list__detail-content">
              {/* 기본 정보 섹션 */}
              <div className="completed-project-list__section">
                <h3 className="completed-project-list__section-title">
                  📌 기본 정보
                </h3>
                <div className="completed-project-list__field">
                  <div className="completed-project-list__field-label">
                    프로젝트 번호
                  </div>
                  <div className="completed-project-list__field-value">
                    {selectedProject.projectNumber}
                  </div>
                </div>
                <div className="completed-project-list__field">
                  <div className="completed-project-list__field-label">
                    광고주명
                  </div>
                  <div className="completed-project-list__field-value">
                    {selectedProject.marketerName}
                  </div>
                </div>
                <div className="completed-project-list__field">
                  <div className="completed-project-list__field-label">
                    광고주 ID
                  </div>
                  <div className="completed-project-list__field-value">
                    {selectedProject.marketerUserId}
                  </div>
                </div>
                <div className="completed-project-list__field">
                  <div className="completed-project-list__field-label">
                    상품명
                  </div>
                  <div className="completed-project-list__field-value">
                    {selectedProject.productName || '-'}
                  </div>
                </div>
                <div className="completed-project-list__field">
                  <div className="completed-project-list__field-label">
                    상태
                  </div>
                  <div className="completed-project-list__field-value completed-project-list__field-value--success">
                    {getStatusText(selectedProject.status)}
                  </div>
                </div>
                <div className="completed-project-list__field">
                  <div className="completed-project-list__field-label">
                    완료일
                  </div>
                  <div className="completed-project-list__field-value completed-project-list__field-value--success">
                    {formatDateTime(selectedProject.completedAt)}
                  </div>
                </div>
              </div>

              {/* 일정 정보 섹션 */}
              {selectedProject.scheduleInfo && (
                <div className="completed-project-list__section">
                  <h3 className="completed-project-list__section-title">
                    📅 일정 정보
                  </h3>
                  <div className="completed-project-list__field">
                    <div className="completed-project-list__field-label">
                      프로젝트 생성일
                    </div>
                    <div className="completed-project-list__field-value">
                      {formatDateTime(selectedProject.scheduleInfo.createdAt)}
                    </div>
                  </div>
                  <div className="completed-project-list__field">
                    <div className="completed-project-list__field-label">
                      모집 및 선정 마감일
                    </div>
                    <div className="completed-project-list__field-value">
                      {formatDateTime(selectedProject.scheduleInfo.recruitEnd)}
                    </div>
                  </div>
                  <div className="completed-project-list__field">
                    <div className="completed-project-list__field-label">
                      결과물 제출 마감일
                    </div>
                    <div className="completed-project-list__field-value">
                      {formatDateTime(selectedProject.scheduleInfo.submitEnd)}
                    </div>
                  </div>
                  {selectedProject.scheduleInfo.settlementDates?.length > 0 && (
                    <div className="completed-project-list__field">
                      <div className="completed-project-list__field-label">
                        CPS 결산일
                      </div>
                      {selectedProject.scheduleInfo.settlementDates.map((date, idx) => (
                        <div key={idx} className="completed-project-list__settlement-date">
                          {idx + 1}회차: {formatDateTime(date)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 결제 및 모집 정보 섹션 */}
              {(selectedProject.paymentDetails || selectedProject.recruitHeadcount !== undefined || selectedProject.contractedCount !== undefined || selectedProject.hasCPS !== undefined) && (
                <div className="completed-project-list__section">
                  <h3 className="completed-project-list__section-title">
                    💰 결제 및 모집 정보
                  </h3>
                  {selectedProject.paymentDetails && (
                    <>
                      <div className="completed-project-list__field">
                        <div className="completed-project-list__field-label">
                          결제 금액
                        </div>
                        <div className="completed-project-list__field-value">
                          {(selectedProject.paymentDetails.deposit || 0).toLocaleString()}pt
                        </div>
                        <div className="completed-project-list__payment-breakdown">
                          (기본운영비: {(selectedProject.paymentDetails.baseOp || 0).toLocaleString()}pt, 
                          리워드: {(selectedProject.paymentDetails.rewardTotal || 0).toLocaleString()}pt
                          {selectedProject.paymentDetails.perBudget > 0 && 
                            `, CPS예산: ${selectedProject.paymentDetails.perBudget.toLocaleString()}pt`
                          })
                        </div>
                      </div>
                      
                      {/* ✅ 환불 정보 추가 */}
                      {selectedProject.refundInfo && (
                        <>
                          <div className="completed-project-list__field">
                            <div className="completed-project-list__field-label">
                              실제 사용 금액
                            </div>
                            <div className="completed-project-list__field-value completed-project-list__field-value--danger">
                              {(selectedProject.refundInfo.totalUsed || 0).toLocaleString()}pt
                            </div>
                            <div className="completed-project-list__payment-breakdown">
                              (운영비: {(selectedProject.refundInfo.breakdown.actualBaseOp || 0).toLocaleString()}pt, 
                              리워드: {(selectedProject.refundInfo.breakdown.actualReward || 0).toLocaleString()}pt
                              {selectedProject.refundInfo.breakdown.actualCPS > 0 && 
                                `, CPS: ${selectedProject.refundInfo.breakdown.actualCPS.toLocaleString()}pt`
                              })
                            </div>
                          </div>
                          
                          <div className="completed-project-list__field">
                            <div className="completed-project-list__field-label">
                              환불 금액
                            </div>
                            <div className="completed-project-list__field-value completed-project-list__field-value--success">
                              {(selectedProject.refundInfo.refundAmount || 0).toLocaleString()}pt
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  <div className="completed-project-list__field">
                    <div className="completed-project-list__field-label">
                      모집 인원
                    </div>
                    <div className="completed-project-list__field-value">
                      {selectedProject.recruitHeadcount || 0}명
                    </div>
                  </div>
                  <div className="completed-project-list__field">
                    <div className="completed-project-list__field-label">
                      계약 인원
                    </div>
                    <div className="completed-project-list__field-value completed-project-list__field-value--success">
                      {selectedProject.contractedCount || 0}명
                    </div>
                  </div>
                  <div className="completed-project-list__field">
                    <div className="completed-project-list__field-label">
                      구매당 리워드 설정
                    </div>
                    <div className={`completed-project-list__field-value ${
                      selectedProject.hasCPS ? 'completed-project-list__field-value--purple' : 'completed-project-list__field-value--gray'
                    }`}>
                      {selectedProject.hasCPS ? '✅ 설정됨' : '❌ 미설정'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="completed-project-list__detail-empty">
            프로젝트를 선택해주세요.
          </div>
        )}
      </div>

      {/* 프로젝트 상세보기 모달 */}
      <ProjectDetailModal 
        open={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        project={selectedProject?.fullProjectData}
      />

      {/* 인플루언서 선정 계약 상세 모달 */}
      <ApplicationsViewModal
        open={showApplicationsModal}
        onClose={() => setShowApplicationsModal(false)}
        projectId={selectedProject?.id}
        projectNumber={selectedProject?.projectNumber}
      />

      {/* 결과물 상태보기 모달 */}
      <ResultStatusModal
        open={showResultModal}
        onClose={() => setShowResultModal(false)}
        projectId={selectedProject?.id}
        projectNumber={selectedProject?.projectNumber}
      />

      {/* CPS 결산 상태보기 모달 */}
      <CPSSettlementModal
        open={showCPSModal}
        onClose={() => setShowCPSModal(false)}
        projectId={selectedProject?.id}
        projectNumber={selectedProject?.projectNumber}
      />

      {/* 이력 관리 모달 */}
      <ProjectHistoryModal
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        projectId={selectedProject?.id}
        projectNumber={selectedProject?.projectNumber}
      />
    </div>
  );
}

